import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverStateV2 } from './useDriverStateV2';
import env from '../config/env';

interface CreateCallParams {
  account: string; // Customer ID
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  dispatchNumber: string;
  contactName: string;
  contactPhone: string;
  pickupLocation: string;
  pickupLocationLat?: number;
  pickupLocationLong?: number;
  destination?: string;
  destinationLat?: number;
  destinationLong?: number;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleYear?: number;
  vehicleColor?: string;
  licensePlate?: string;
  vin?: string;
  drivers?: string[];
  notes?: string;
  billingNotes?: string;
  includeBillingNotes?: boolean;
  isImpound?: boolean;
  rateId?: string;
}

export const useCreateCall = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateCallParams) => {
      if (!driver?.companyId) {
        throw new Error('Company ID not available');
      }

      const token = await getAccessTokenSilently();

      const response = await fetch(`${env.apiUrl}/calls/new-call`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-company-id': driver.companyId,
        },
        body: JSON.stringify({
          ...params,
          vehicleType: 'LIGHT_DUTY', // Default vehicle type for driver app
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create call: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate both driver calls and company dispatches caches
      queryClient.invalidateQueries({ queryKey: ['driver-calls'] });
      queryClient.invalidateQueries({ queryKey: ['company-dispatches'] });
    },
    onError: (error) => {
      console.error('Failed to create call:', error);
    },
  });
};








